import { Form, Head } from "@inertiajs/react"
import { Camera, CheckCircle2, ShieldCheck } from "lucide-react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { withPublicLayout } from "@/layouts/public-layout"
import { submitPath } from "@/routes"

export default function SubmitSite() {
  return (
    <>
      <Head title="Submit a site" />
      <div className="mx-auto grid max-w-[1100px] gap-12 px-4 pt-14 sm:px-6 lg:grid-cols-[1fr_360px]">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight">
            Submit a single-serving site
          </h1>
          <p className="text-muted-foreground mt-3 max-w-xl">
            One domain, one page, one idea. Yours or someone else&apos;s. Only
            the URL is required. We pull the title and description from the page
            if you leave them blank.
          </p>

          <Form
            method="post"
            action={submitPath()}
            resetOnSuccess
            className="mt-10 grid gap-6"
          >
            {({ processing, errors }) => (
              <>
                <Field label="URL" error={errors.url} htmlFor="url">
                  <Input
                    id="url"
                    name="url"
                    required
                    autoFocus
                    inputMode="url"
                    placeholder="isitchristmas.com"
                    className="h-11 font-mono"
                  />
                </Field>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field
                    label="Title"
                    error={errors.title}
                    htmlFor="title"
                    optional
                  >
                    <Input id="title" name="title" maxLength={120} />
                  </Field>
                  <Field
                    label="Made by"
                    error={errors.author_name}
                    htmlFor="author_name"
                    optional
                  >
                    <Input
                      id="author_name"
                      name="author_name"
                      maxLength={120}
                    />
                  </Field>
                </div>
                <Field
                  label="What does it do?"
                  error={errors.description}
                  htmlFor="description"
                  optional
                >
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    maxLength={500}
                    className="border-input bg-card focus-visible:border-ring focus-visible:ring-ring/50 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
                  />
                </Field>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field
                    label="Maker's website"
                    error={errors.author_url}
                    htmlFor="author_url"
                    optional
                  >
                    <Input id="author_url" name="author_url" inputMode="url" />
                  </Field>
                  <Field
                    label="Your email"
                    error={errors.submitter_email}
                    htmlFor="submitter_email"
                    optional
                    hint="Only used if we have a question."
                  >
                    <Input
                      id="submitter_email"
                      name="submitter_email"
                      type="email"
                    />
                  </Field>
                </div>

                {/* Honeypot. Hidden from people, irresistible to bots. */}
                <div aria-hidden className="absolute -left-[9999px]">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <Button
                    type="submit"
                    disabled={processing}
                    className="bg-brand hover:bg-brand-hover h-11 rounded-xl px-6 text-white"
                  >
                    {processing && <Spinner />}
                    Submit site
                  </Button>
                </div>
              </>
            )}
          </Form>
        </div>

        <aside className="bg-muted/60 h-fit space-y-5 rounded-2xl border p-6 text-sm">
          <h2 className="font-bold">What happens next</h2>
          <Step icon={<ShieldCheck className="size-4" />} title="We check it">
            A request goes out within a minute to confirm the site is up and not
            a parked domain.
          </Step>
          <Step icon={<Camera className="size-4" />} title="We screenshot it">
            Headless Chrome loads it, waits for it to settle, and captures a
            1280×800 frame.
          </Step>
          <Step
            icon={<CheckCircle2 className="size-4" />}
            title="A human approves it"
          >
            No ads, no storefronts, no multi-page sites, no crypto. Just the one
            good idea.
          </Step>
        </aside>
      </div>
    </>
  )
}

function Field({
  label,
  htmlFor,
  error,
  optional,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  optional?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={htmlFor}>
        {label}
        {optional && (
          <span className="text-muted-foreground font-normal">optional</span>
        )}
      </Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      <InputError message={error} />
    </div>
  )
}

function Step({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="flex gap-3">
      <span className="bg-brand-soft text-brand mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
        {icon}
      </span>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-muted-foreground">{children}</p>
      </div>
    </div>
  )
}

SubmitSite.layout = withPublicLayout
